import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Building2, Download, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  getBankAccounts,
  createBankAccount,
  updateBankAccount,
  deleteBankAccount,
  getBankTransactions,
  formatCurrency,
  handleNumericInput,
  parseNumericValue,
} from "../lib/api";
import { COMPANIES, COMPANY_OPTIONS, getCompanyLabel, COMPANY_COLORS } from "../lib/companies";
import { toast } from "sonner";
import ExportDialog from "../components/ExportDialog";

const emptyAccount = {
  name: "",
  bank_name: "",
  account_number: "",
  balance: "",
  balance_date: "",
  account_type: "savings",
  company: "empact-studio",
};

export default function BankAccounts() {
  const { canEdit } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formData, setFormData] = useState(emptyAccount);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [accountTransactions, setAccountTransactions] = useState({});

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await getBankAccounts();
      setAccounts(response.data);
    } catch (error) {
      toast.error("Failed to load bank accounts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleToggleTransactions = async (accountId) => {
    if (expandedAccount === accountId) {
      setExpandedAccount(null);
      return;
    }
    setExpandedAccount(accountId);
    if (!accountTransactions[accountId]) {
      try {
        const res = await getBankTransactions(accountId);
        setAccountTransactions(prev => ({ ...prev, [accountId]: res.data }));
      } catch {
        toast.error("Failed to load transactions");
      }
    }
  };

  const handleOpenDialog = (account = null) => {
    if (account) {
      setSelectedAccount(account);
      setFormData({
        name: account.name,
        bank_name: account.bank_name,
        account_number: account.account_number,
        balance: account.balance,
        balance_date: account.balance_date || "",
        account_type: account.account_type,
        company: account.company || "empact-studio",
      });
    } else {
      setSelectedAccount(null);
      setFormData(emptyAccount);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
    setFormData(emptyAccount);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        balance: parseNumericValue(formData.balance),
      };
      
      if (selectedAccount) {
        await updateBankAccount(selectedAccount.id, submitData);
        toast.success("Bank account updated successfully");
      } else {
        await createBankAccount(submitData);
        toast.success("Bank account added successfully");
      }
      handleCloseDialog();
      fetchAccounts();
    } catch (error) {
      toast.error("Failed to save bank account");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBankAccount(selectedAccount.id);
      toast.success("Bank account deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedAccount(null);
      fetchAccounts();
    } catch (error) {
      toast.error("Failed to delete bank account");
    }
  };

  const totalBalance = accounts.reduce((sum, acc) => sum + acc.balance, 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bank-accounts-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Bank Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage your bank account balances
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setExportDialogOpen(true)}
            data-testid="export-bank-accounts"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          {canEdit && (
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              data-testid="add-bank-account-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Account
            </Button>
          )}
        </div>
      </div>

      {/* Summary Card */}
      <Card data-testid="total-balance-card">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-slate-500">Total Balance</p>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900 tabular-nums mt-1">
                {formatCurrency(totalBalance)}
              </p>
            </div>
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts Grid */}
      {accounts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map((account) => (
            <Card key={account.id} className="card-hover" data-testid={`account-card-${account.id}`}>
              <CardHeader className="flex flex-row items-start justify-between pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold">
                      {account.name}
                    </CardTitle>
                    <p className="text-xs text-slate-500">{account.bank_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${COMPANY_COLORS[account.company] || COMPANY_COLORS.all}`} data-testid={`account-company-${account.id}`}>
                    {getCompanyLabel(account.company)}
                  </span>
                {canEdit && (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleOpenDialog(account)}
                    data-testid={`edit-account-${account.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500 hover:text-red-600"
                    onClick={() => {
                      setSelectedAccount(account);
                      setDeleteDialogOpen(true);
                    }}
                    data-testid={`delete-account-${account.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Account No.</span>
                    <span className="font-mono text-slate-700">
                      ****{account.account_number.slice(-4)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Type</span>
                    <span className="capitalize text-slate-700">
                      {account.account_type}
                    </span>
                  </div>
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-lg sm:text-2xl font-bold text-slate-900 tabular-nums">
                      {formatCurrency(account.balance)}
                    </p>
                    {account.balance_date && (
                      <p className="text-xs text-slate-400 mt-0.5">as of {new Date(account.balance_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    )}
                  </div>
                  {/* Monthly Transactions Toggle */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-2 text-xs text-slate-500 hover:text-slate-700 gap-1"
                    onClick={() => handleToggleTransactions(account.id)}
                    data-testid={`toggle-txns-${account.id}`}
                  >
                    {expandedAccount === account.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    Monthly Inflows / Outflows
                  </Button>
                  {expandedAccount === account.id && (
                    <div className="mt-2 space-y-3" data-testid={`txns-panel-${account.id}`}>
                      {accountTransactions[account.id]?.months?.length > 0 ? (
                        accountTransactions[account.id].months.map((m) => (
                          <div key={m.month} className="rounded-lg bg-slate-50 border border-slate-100 overflow-hidden">
                            {/* Month Header */}
                            <div className="flex items-center justify-between px-3 py-2 bg-slate-100">
                              <span className="text-xs font-semibold text-slate-700">
                                {new Date(m.month + "-01").toLocaleDateString("en-IN", { year: "numeric", month: "long" })}
                              </span>
                              <span className="text-[10px] text-slate-500">{m.transactions?.length || 0} txns</span>
                            </div>
                            {/* Inflow / Outflow Summary */}
                            <div className="grid grid-cols-2 gap-px bg-slate-200">
                              <div className="bg-white px-3 py-2">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wide">Inflow</p>
                                <p className="text-sm font-semibold text-emerald-600 tabular-nums">{formatCurrency(m.inflows)}</p>
                              </div>
                              <div className="bg-white px-3 py-2">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wide">Outflow</p>
                                <p className="text-sm font-semibold text-red-500 tabular-nums">{formatCurrency(m.outflows)}</p>
                              </div>
                            </div>
                            {/* Transaction List */}
                            {m.transactions?.length > 0 && (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-t border-slate-200 bg-slate-50">
                                    <th className="text-left px-3 py-1 font-medium text-slate-400 w-20">Date</th>
                                    <th className="text-left px-2 py-1 font-medium text-slate-400">Description</th>
                                    <th className="text-right px-3 py-1 font-medium text-slate-400 w-28">Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {m.transactions.map((t, idx) => (
                                    <tr key={idx} className="border-t border-slate-100">
                                      <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{t.date?.slice(5) || "-"}</td>
                                      <td className="px-2 py-1.5 text-slate-600 truncate max-w-[150px]">{t.description}</td>
                                      <td className={`px-3 py-1.5 text-right font-medium tabular-nums whitespace-nowrap ${t.type === "inflow" ? "text-emerald-600" : "text-red-500"}`}>
                                        {t.type === "inflow" ? "+" : "-"}{formatCurrency(t.amount)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-center text-slate-400 py-3">No transactions recorded yet</p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="empty-state">
              <Building2 className="empty-state-icon" />
              <h3 className="empty-state-title">No bank accounts yet</h3>
              <p className="empty-state-text">
                Add your first bank account to start tracking balances
              </p>
              <Button
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="bank-account-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedAccount ? "Edit Bank Account" : "Add Bank Account"}
            </DialogTitle>
            <DialogDescription>
              {selectedAccount ? "Update your bank account details below." : "Fill in the details to add a new bank account."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="company">Company</Label>
              <Select
                value={formData.company}
                onValueChange={(value) => setFormData({ ...formData, company: value })}
              >
                <SelectTrigger data-testid="select-bank-company">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_OPTIONS.map((company) => (
                    <SelectItem key={company.value} value={company.value}>
                      {company.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="name">Account Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Business Account"
                required
                data-testid="input-account-name"
              />
            </div>
            <div>
              <Label htmlFor="bank_name">Bank Name</Label>
              <Input
                id="bank_name"
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="e.g., HDFC Bank"
                required
                data-testid="input-bank-name"
              />
            </div>
            <div>
              <Label htmlFor="account_number">Account Number</Label>
              <Input
                id="account_number"
                value={formData.account_number}
                onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                placeholder="e.g., 1234567890"
                required
                data-testid="input-account-number"
              />
            </div>
            <div>
              <Label htmlFor="balance">Current Balance (₹)</Label>
              <Input
                id="balance"
                type="text"
                inputMode="decimal"
                value={formData.balance}
                onChange={(e) => setFormData({ ...formData, balance: handleNumericInput(e.target.value) })}
                placeholder="Enter balance"
                required
                data-testid="input-balance"
              />
            </div>
            <div>
              <Label htmlFor="balance_date">Balance as of Date</Label>
              <Input
                id="balance_date"
                type="date"
                value={formData.balance_date}
                onChange={(e) => setFormData({ ...formData, balance_date: e.target.value })}
                data-testid="input-balance-date"
              />
            </div>
            <div>
              <Label htmlFor="account_type">Account Type</Label>
              <Select
                value={formData.account_type}
                onValueChange={(value) => setFormData({ ...formData, account_type: value })}
              >
                <SelectTrigger data-testid="select-account-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="savings">Savings</SelectItem>
                  <SelectItem value="current">Current</SelectItem>
                  <SelectItem value="fixed_deposit">Fixed Deposit</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" className="bg-brand-orange hover:bg-[#e09515] text-brand-navy" data-testid="save-bank-account">
                {selectedAccount ? "Update" : "Add Account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Bank Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedAccount?.name}"? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-account"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        dataType="bank-accounts"
        title="Bank Accounts"
        showDateFilter={false}
        showCompanyFilter={true}
      />
    </div>
  );
}
